import { useMemo } from 'react';

import { getNodes, sortingToGql, useExplorationDataQuery } from '@/graphql';
import { flattenNestedList } from '@/explore/grouping';
import { Exploration, Field, Pipeline, QueryVariables, Visualisation } from '@/explore/types';
import { filterVariablesForPipeline } from '@/explore/utils';
import { useMemoizeDataQueryVariables } from '@/explore/utils/use-memoize-data-query-variables';
import { useAccountContext } from '@/lib/accounts/context';
import { useExplorationCellContext } from '@/explore/exploration/exploration-cell-context';

import {
  addPeriodToDateOperationsToPipeline,
  getQueryForVisualisation,
  isBigNumberWithPeriodToDateComparison,
} from '../utils';

interface UsePeriodToDateQueryProps {
  pipeline: Pipeline;
  fields: Field[];
  visualisation: Visualisation;
  exploration: Exploration;
  variables: QueryVariables;
  mainAxisField?: Field;
  skip: boolean;
}

const DEFAULT_PERIOD_TO_DATE_LIMIT = 50; // Will have to change if custom larger offsets are supported in the future

export const usePeriodToDateQuery = ({
  pipeline,
  fields,
  visualisation,
  exploration,
  variables,
  mainAxisField,
  skip,
}: UsePeriodToDateQueryProps) => {
  const { account, isFeatureEnabled } = useAccountContext();
  const { cell } = useExplorationCellContext();

  // Check if period-to-date is enabled
  const isPeriodToDateEnabled =
    isBigNumberWithPeriodToDateComparison(visualisation.viewOptions?.bigNumber) &&
    mainAxisField?.precision !== undefined;

  // Create period-to-date pipeline and query
  const periodToDateQuery = useMemo(() => {
    if (!isPeriodToDateEnabled || !mainAxisField?.precision) {
      return null;
    }

    const basePipeline = addPeriodToDateOperationsToPipeline(
      pipeline,
      mainAxisField.key,
      mainAxisField.precision,
    );

    const query = getQueryForVisualisation({
      pipeline: basePipeline,
      fields,
      visualisation,
      exploration,
    });

    return {
      ...query,
      limit: DEFAULT_PERIOD_TO_DATE_LIMIT,
    };
  }, [isPeriodToDateEnabled, pipeline, mainAxisField, fields, visualisation, exploration]);

  // Execute period-to-date query
  const pipelineVariables = useMemo(() => {
    if (!periodToDateQuery?.queryPipeline) {
      return [];
    }
    return filterVariablesForPipeline(periodToDateQuery.queryPipeline, variables);
  }, [periodToDateQuery?.queryPipeline, variables]);

  const {
    data: periodToDateData,
    error: periodToDateError,
    loading: isPeriodToDateLoading,
  } = useExplorationDataQuery({
    variables: useMemoizeDataQueryVariables({
      baseModelId: periodToDateQuery?.queryPipeline?.baseModelId ?? '',
      accountId: account.accountId,
      pipeline: periodToDateQuery?.queryPipeline?.operations ?? [],
      sort: periodToDateQuery !== null ? sortingToGql(periodToDateQuery.sort) : [],
      first: periodToDateQuery?.limit,
      variables: pipelineVariables,
      options: {
        labels: { exploration: exploration.explorationId, block: cell.id },
        optimize: isFeatureEnabled('optimizeQuery'),
      },
    }),
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-first',
    skip: skip || !isPeriodToDateEnabled,
    onError: (error) => {
      console.warn('Period-to-date query failed:', error);
    },
  });

  // Process period-to-date data
  const periodToDateFlattenedRecords = useMemo(() => {
    if (!isPeriodToDateEnabled || !periodToDateData) {
      return [];
    }

    return flattenNestedList(getNodes(periodToDateData?.account?.query));
  }, [isPeriodToDateEnabled, periodToDateData]);

  return {
    isPeriodToDateLoading,
    periodToDateError,
    periodToDateFlattenedRecords,
  };
};
